import { money } from '../financial/formatting'

// ─────────────────────────────────────────────────────────────────────────────
//   Piezas sin JSX de la pantalla de Company finances.
//
//   En un `.js` y no en un `.jsx` por lo mismo que `financial/formatting.js`:
//   `react-refresh/only-export-components` avisa de cualquier export que no sea
//   un componente, y ninguna de estas lo es.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//   null no es 0, y `money()` no sabe la diferencia
//
//   `money()` hace `Number(v || 0)`, así que un null sale '$0.00'. En el resto
//   de la app da igual —los importes de un movimiento siempre existen— pero
//   `/corporate/overview` está construido entero sobre esa distinción:
//   `tax.reserve_estimated`, los cinco tramos de la cascada, `by_owner[].amount`
//   y `not_counted[].amount` son `float | None` en el esquema, y el backend
//   documenta que el null significa "no se pudo calcular" mientras que el 0
//   significa "se calculó y la respuesta es ninguno". Son respuestas opuestas
//   que se ven idénticas en cuanto una se pinta como la otra.
//
//   De ahí que esto exista en vez de llamar a `money()` con un `?? 0` delante:
//   el `?? 0` es exactamente el bug, escrito de forma que parece un default.
// ─────────────────────────────────────────────────────────────────────────────
export const moneyOrBlank = (value, blank = '—') =>
  value === null || value === undefined ? blank : money(value)

// ─────────────────────────────────────────────────────────────────────────────
//   Meses en inglés, escritos aquí
//
//   `<input type="month">` y `toLocaleDateString()` rinden el locale del
//   NAVEGADOR, no el de la interfaz: en un Chrome en español la pantalla decía
//   "septiembre de 2026" en medio de una UI en inglés. Los nombres van escritos
//   porque el idioma de esta app es una decisión de la app, no del equipo del
//   usuario.
// ─────────────────────────────────────────────────────────────────────────────
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// 'YYYY-MM' → { year, month } con month 1..12, o nulls si la cadena no lo es.
// Aritmética de cadena y nunca `new Date(ym)`, por lo mismo que documenta
// `fmtDay` en financial/formatting.js: el mes que se está mirando es un dato,
// no un instante, y no puede moverse según el huso del navegador.
export const splitMonth = (ym) => {
  const [y, m] = String(ym ?? '').split('-').map(Number)
  if (!y || !m || m < 1 || m > 12) return { year: null, month: null }
  return { year: y, month: m }
}

export const joinMonth = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}`

// El nombre del mes en inglés. Sustituye al `toLocaleDateString('en-US', …)`
// que había en la página: aquel forzaba el locale en la llamada y seguía
// dependiendo de que nadie olvidara el argumento en el siguiente call site.
export const monthLabel = (ym) => {
  const { year, month } = splitMonth(ym)
  if (!year) return String(ym ?? '')
  return `${MONTH_NAMES[month - 1]} ${year}`
}

// ─────────────────────────────────────────────────────────────────────────────
//   Porcentajes de la política
//
//   Los `*_pct` de `AllocationPolicy` llegan como fracción (0.3), y esto los
//   rinde como '30%'. Es formateo de unidad, no cálculo: la cifra en dólares de
//   cada tramo la calcula el backend y esta función nunca la toca.
//
//   Los porcentajes se pintan SIEMPRE desde el objeto `policy` que devuelve el
//   endpoint. Escribirlos en el componente haría que la pantalla siguiera
//   diciendo 30% el día que el contable diga 25% — y `AllocationPolicy` está
//   versionada por fecha y nunca se edita en sitio precisamente para que un mes
//   ya reportado conserve los porcentajes que estaban en vigor cuando se
//   calculó. Un literal aquí rompería esa garantía desde el otro lado.
// ─────────────────────────────────────────────────────────────────────────────
export const pctOrBlank = (value, blank = '—') =>
  value === null || value === undefined
    ? blank
    : `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`

// ─────────────────────────────────────────────────────────────────────────────
//   Respuestas del backend que no son fallos
//
//   Vivían en `CompanyFinancesPage.jsx`; suben aquí porque el modal de pago de
//   un recurrente también las necesita, y dos copias de "cómo se lee el detail
//   de un rechazo" es una para quedarse atrás.
// ─────────────────────────────────────────────────────────────────────────────
export const isForbidden = (err) => err?.response?.status === 403

// El `detail` del backend, literal, y el fallback sólo si no lo trae. Los
// rechazos de `corporate/` explican el motivo real —DerivedCategoryNotWritable,
// CatalogRowInactive, RecurringPeriodAlreadyRegistered— mejor de lo que puede
// una pantalla que no conoce sus reglas.
export const errorDetail = (err, fallback) => {
  const detail = err?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

// ─────────────────────────────────────────────────────────────────────────────
//   Etiquetar la ventana, no el mes
//
//   Desde que la pantalla pide `?from=&to=`, casi todo lo que se pinta es el
//   agregado de un RANGO y no de un mes. Un encabezado que siga diciendo "in
//   September 2026" sobre siete meses de gasto es la forma más barata de que
//   alguien se lleve una cifra equivocada, así que el rótulo sale de los dos
//   extremos y colapsa solo cuando son el mismo mes — que es el caso normal y
//   se sigue leyendo exactamente como antes.
//
//   Comparación por igualdad de cadena y no por fechas: 'YYYY-MM' con el mes a
//   dos dígitos ordena lexicográficamente igual que cronológicamente, y no hace
//   falta construir un Date —que además metería el huso del navegador en una
//   decisión que no es un instante— para saber si los dos extremos coinciden.
// ─────────────────────────────────────────────────────────────────────────────
export const rangeLabel = (from, to) =>
  from === to ? monthLabel(from) : `${monthLabel(from)} – ${monthLabel(to)}`

// ─────────────────────────────────────────────────────────────────────────────
//   El signo, como color
//
//   Devuelve el token que le toca a una cifra con signo, y `undefined` cuando
//   no hay cifra. Ese `undefined` es el punto: un null aquí NO es un cero, y
//   pintarlo de verde "porque no es negativo" afirmaría que se calculó y salió
//   a favor. Sin cifra no hay signo, y sin signo no hay color.
// ─────────────────────────────────────────────────────────────────────────────
export const signTone = (value) => {
  if (value === null || value === undefined) return undefined
  return value < 0 ? 'var(--red)' : 'var(--green)'
}
